import React, { useState, useRef, useCallback, useEffect } from "react";
import {
  ArrowLeft,
  Plus,
  Trash2,
  Users,
  Zap,
  Target,
  ChevronDown,
  MessageSquare,
  Save
} from "lucide-react";

/* ── Reusable Custom Select (matches app-wide design) ── */
const CustomSelect = ({ options, value, onChange, style, className = "", badgeMap = {} }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className={`relative ${className}`} style={style} ref={containerRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between px-4 py-2.5 text-[11px] border rounded-xl transition-all duration-200 font-bold uppercase tracking-wider h-[38px] ${
          isOpen
            ? "bg-white border-gray-900 ring-4 ring-gray-900/5 shadow-sm"
            : "bg-gray-50 border-gray-100 text-gray-700 hover:bg-white hover:border-gray-200"
        }`}
      >
        <span className="truncate flex items-center gap-1.5">
          {value}
          {badgeMap[value] && (
            <span className="inline-flex px-1.5 py-0.5 text-[7px] font-black uppercase tracking-widest rounded-md bg-indigo-100 text-indigo-600 leading-none">
              {badgeMap[value]}
            </span>
          )}
        </span>
        <ChevronDown
          size={14}
          className={`text-gray-400 transition-transform duration-300 flex-shrink-0 ml-2 ${isOpen ? "rotate-180 text-gray-900" : ""}`}
        />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 w-full mt-2 bg-white border border-gray-100 rounded-xl shadow-xl z-[100] overflow-hidden animate-slideIn">
          {options.map((opt) => (
            <div
              key={opt}
              onClick={() => { onChange(opt); setIsOpen(false); }}
              className={`px-4 py-2.5 text-[11px] font-bold cursor-pointer transition-colors flex items-center justify-between gap-2 ${
                value === opt
                  ? "bg-gray-900 text-white"
                  : "text-gray-600 hover:bg-gray-50"
              }`}
            >
              <span className="truncate">{opt}</span>
              {badgeMap[opt] && (
                <span className={`flex-shrink-0 inline-flex px-1.5 py-0.5 text-[7px] font-black uppercase tracking-widest rounded-md leading-none ${
                  value === opt
                    ? "bg-white/20 text-white"
                    : "bg-indigo-100 text-indigo-600"
                }`}>
                  {badgeMap[opt]}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ── Main Component ── */
function ConditionEditor({ campaignData, onBack, onSave, customParams = [] }) {
  const containerRef = useRef(null);
  const [sidebarWidth, setSidebarWidth] = useState(950);
  const [isResizing, setIsResizing] = useState(false);
  const [conditions, setConditions] = useState(() => {
    const cond = campaignData?.conditions || {};
    return {
      audience: cond.audience || "all",
      delay: cond.delay !== undefined ? cond.delay : 5,
      priority: cond.priority || 0,
      matchType: cond.matchType || "all",
      rules: cond.rules || [],
    };
  });

  const startResizing = useCallback(() => setIsResizing(true), []);
  const stopResizing = useCallback(() => setIsResizing(false), []);

  const resize = useCallback(
    (e) => {
      if (isResizing && containerRef.current) {
        const newWidth = e.clientX - containerRef.current.getBoundingClientRect().left;
        const maxWidth = window.innerWidth - 100;
        if (newWidth > 340 && newWidth < maxWidth) setSidebarWidth(newWidth);
      }
    },
    [isResizing]
  );

  useEffect(() => {
    if (isResizing) {
      window.addEventListener("mousemove", resize);
      window.addEventListener("mouseup", stopResizing);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    } else {
      window.removeEventListener("mousemove", resize);
      window.removeEventListener("mouseup", stopResizing);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    }
    return () => {
      window.removeEventListener("mousemove", resize);
      window.removeEventListener("mouseup", stopResizing);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [isResizing, resize, stopResizing]);

  /* ── Data helpers ── */
  // Show only custom parameters from the backend plus "Custom..." option in the dropdown
  const properties = [...customParams, "Custom..."];
  // Badge map is empty since we removed all inbuilt properties from the dropdown options
  const propertyBadgeMap = {};
  const operators = ["is", "is not", "greater than", "less than", "contains"];

  const applyAudiencePreset = (type) => {
    setConditions((prev) => ({ ...prev, audience: type }));
  };

  const handleAddRule = () =>
    setConditions((prev) => ({
      ...prev,
      rules: [...prev.rules, { property: properties[0] || "Custom...", operator: "is", value: "" }],
    }));

  const handleRemoveRule = (index) =>
    setConditions((prev) => ({ ...prev, rules: prev.rules.filter((_, i) => i !== index) }));

  const updateRule = (index, field, value) => {
    const newRules = [...conditions.rules];
    newRules[index] = { ...newRules[index], [field]: value };
    setConditions((prev) => ({ ...prev, rules: newRules }));
  };

  const audienceOptions = [
    { key: "all", label: "All Visitors", icon: <Users size={14} /> },
    { key: "first_time", label: "New Visitors", icon: <Zap size={14} /> },
    { key: "returning", label: "Returning", icon: <Target size={14} /> },
  ];

  return (
    <div
      className="flex flex-col"
      style={{ height: "calc(100vh - 84px)", background: "#f8fafc" }}
      ref={containerRef}
    >
      {/* ── Header ── */}
      <header className="h-[68px] bg-white border-b border-gray-100 flex items-center justify-between px-6 flex-shrink-0 shadow-sm">
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="w-9 h-9 flex items-center justify-center border border-gray-100 rounded-xl text-gray-400 hover:text-gray-900 hover:bg-gray-50 hover:border-gray-200 transition-all"
          >
            <ArrowLeft size={16} />
          </button>
          <div className="flex flex-col gap-0.5">
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
              Targeting & Behavior
            </span>
            <span className="text-sm font-black text-gray-900 uppercase tracking-tight">
              {campaignData?.name || "Untitled Campaign"}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="px-5 py-2 text-[10px] font-bold uppercase tracking-widest text-gray-500 border border-gray-100 rounded-xl hover:bg-gray-50 transition-all"
          >
            Back to Content
          </button>
          <button
            onClick={() => onSave({ ...campaignData, conditions })}
            className="flex items-center gap-2 px-6 py-2 bg-gray-900 text-white text-[10px] font-bold uppercase tracking-widest rounded-xl hover:bg-black transition-all shadow-lg shadow-gray-900/20 active:scale-95"
          >
            <Save size={14} /> Finish & Save
          </button>
        </div>
      </header>

      {/* ── Body ── */}
      <div className="flex flex-1 overflow-hidden">
        {/* ── Left Sidebar ── */}
        <aside
          className="bg-white border-r border-gray-100 overflow-y-auto flex-shrink-0"
          style={{ width: sidebarWidth }}
        >
          <div className="p-8 space-y-10">
            {/* ── Section 1: Audience ── */}
            <section>
              <h2 className="text-sm font-black text-gray-900 uppercase tracking-widest flex items-center gap-2 mb-1">
                <Users size={15} /> Select Audience
              </h2>
              <p className="text-[11px] font-medium text-gray-400 mb-5">
                Reach the right visitors with ease.
              </p>

              <div className="grid grid-cols-3 gap-2 bg-gray-50 p-1.5 rounded-2xl border border-gray-100">
                {audienceOptions.map(({ key, label, icon }) => (
                  <button
                    key={key}
                    onClick={() => applyAudiencePreset(key)}
                    className={`flex items-center justify-center gap-2 py-2.5 rounded-xl text-[10px] font-bold uppercase tracking-widest transition-all ${
                      conditions.audience === key
                        ? "bg-white text-gray-900 shadow-sm border border-gray-100"
                        : "text-gray-400 hover:text-gray-600"
                    }`}
                  >
                    {icon} {label}
                  </button>
                ))}
              </div>
            </section>

            {/* ── Section 2: Priority ── */}
            <section>
              <h2 className="text-sm font-black text-gray-900 uppercase tracking-widest mb-1">
                Priority
              </h2>
              <p className="text-[11px] font-medium text-gray-400 mb-5">
                Which campaign shows first if multiple match?
              </p>
              <div className="flex gap-2">
                {[0, 1, 2, 3].map((p) => (
                  <button
                    key={p}
                    onClick={() => setConditions((prev) => ({ ...prev, priority: p }))}
                    className={`flex-1 py-2.5 rounded-xl text-[10px] font-bold uppercase tracking-widest transition-all border ${
                      (conditions.priority || 0) === p
                        ? "bg-gray-900 text-white border-gray-900 shadow-md"
                        : "bg-gray-50 text-gray-400 border-gray-100 hover:bg-white hover:border-gray-200 hover:text-gray-600"
                    }`}
                  >
                    {p === 0 ? "Normal" : `P${p}`}
                  </button>
                ))}
              </div>
            </section>

            {/* ── Section 3: Trigger ── */}
            <section>
              <h2 className="text-sm font-black text-gray-900 uppercase tracking-widest flex items-center gap-2 mb-1">
                <Zap size={15} /> Automatic Trigger
              </h2>
              <p className="text-[11px] font-medium text-gray-400 mb-5">
                Define when your message pops up.
              </p>

              {/* Time Delay */}
              <div className="flex items-center gap-4 bg-gray-50 px-5 py-4 rounded-2xl border border-gray-100 mb-8">
                <div className="w-10 h-10 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Zap size={18} />
                </div>
                <div className="flex-1">
                  <div className="text-xs font-bold text-gray-900 uppercase tracking-wider">Time Delay</div>
                  <div className="text-[10px] text-gray-400 font-medium">Wait after page load</div>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={conditions.delay}
                    onChange={(e) =>
                      setConditions((prev) => ({ ...prev, delay: parseInt(e.target.value) || 0 }))
                    }
                    className="w-16 px-3 py-2 text-center text-sm font-bold text-gray-900 border border-gray-100 rounded-xl bg-white focus:outline-none focus:ring-8 focus:ring-gray-900/5 focus:border-gray-900 transition-all"
                  />
                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">sec</span>
                </div>
              </div>

              {/* Targeting Rules header */}
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xs font-black text-gray-900 uppercase tracking-widest flex items-center gap-2">
                  <Target size={13} /> Targeting Rules
                </h3>
              </div>

              {/* Column headers */}
              {conditions.rules.length > 0 && (
                <div className="grid grid-cols-12 gap-3 mb-2 px-1">
                  <div className="col-span-5 text-[9px] font-bold text-gray-300 uppercase tracking-widest">
                    Property
                  </div>
                  <div className="col-span-3 text-[9px] font-bold text-gray-300 uppercase tracking-widest">
                    Condition
                  </div>
                  <div className="col-span-3 text-[9px] font-bold text-gray-300 uppercase tracking-widest">
                    Value
                  </div>
                  <div className="col-span-1" />
                </div>
              )}

              {/* Rule cards */}
              <div className="space-y-3">
                {conditions.rules.map((rule, idx) => (
                  <div
                    key={idx}
                    className="grid grid-cols-12 gap-3 items-center bg-white p-3 rounded-2xl border border-gray-100 hover:border-gray-200 hover:shadow-sm transition-all"
                  >
                    {/* Property */}
                    <div className="col-span-5">
                      {rule.property === "Custom..." || rule.isCustom ? (
                        <div className="relative">
                          <input
                            className="w-full px-3 py-2 text-[11px] font-bold border border-gray-100 rounded-xl bg-gray-50 focus:outline-none focus:ring-8 focus:ring-gray-900/5 focus:border-gray-900 focus:bg-white transition-all uppercase tracking-wider pr-8"
                            value={rule.customName || ""}
                            onChange={(e) => updateRule(idx, "customName", e.target.value)}
                            placeholder="Property name..."
                            autoFocus
                          />
                          <button
                            onClick={() => {
                              const newRules = [...conditions.rules];
                              newRules[idx] = { ...newRules[idx], property: properties[0], isCustom: false };
                              setConditions((prev) => ({ ...prev, rules: newRules }));
                            }}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                            title="Back to presets"
                          >
                            ↺
                          </button>
                        </div>
                      ) : (
                        <CustomSelect
                          options={properties}
                          value={rule.property}
                          badgeMap={propertyBadgeMap}
                          onChange={(val) => {
                            if (val === "Custom...") {
                              const newRules = [...conditions.rules];
                              newRules[idx] = { ...newRules[idx], property: "Custom...", isCustom: true, customName: "" };
                              setConditions((prev) => ({ ...prev, rules: newRules }));
                            } else {
                              updateRule(idx, "property", val);
                            }
                          }}
                        />
                      )}
                    </div>


                    {/* Operator */}
                    <div className="col-span-3">
                      <CustomSelect
                        options={operators}
                        value={rule.operator}
                        onChange={(val) => updateRule(idx, "operator", val)}
                      />
                    </div>

                    {/* Value */}
                    <div className="col-span-3">
                      <input
                        className="w-full px-3 py-2 text-[11px] font-bold border border-gray-100 rounded-xl bg-gray-50 focus:outline-none focus:ring-8 focus:ring-gray-900/5 focus:border-gray-900 focus:bg-white transition-all uppercase tracking-wider h-[38px]"
                        value={rule.value}
                        onChange={(e) => updateRule(idx, "value", e.target.value)}
                        placeholder="Value..."
                      />
                    </div>

                    {/* Delete */}
                    <div className="col-span-1 flex justify-center">
                      <button
                        onClick={() => handleRemoveRule(idx)}
                        className="w-8 h-8 flex items-center justify-center text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
                        title="Delete rule"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Add rule button */}
              <button
                onClick={handleAddRule}
                className="w-full mt-4 py-3 flex items-center justify-center gap-2 text-[10px] font-bold uppercase tracking-widest text-gray-400 bg-gray-50 border border-dashed border-gray-200 rounded-2xl hover:text-gray-900 hover:bg-white hover:border-gray-300 transition-all"
              >
                <Plus size={13} /> Add Dynamic Condition
              </button>
            </section>
          </div>
        </aside>

        {/* ── Resizer ── */}
        <div
          className={`w-1 flex-shrink-0 cursor-col-resize relative group transition-colors ${
            isResizing ? "bg-indigo-500" : "bg-gray-100 hover:bg-indigo-400"
          }`}
          onMouseDown={startResizing}
        />

        {/* ── Right Panel: Summary ── */}
        <main className="flex-1 flex items-center justify-center p-10 bg-[#f8fafc] overflow-auto">
          <div className="text-center max-w-sm w-full font-['Inter',sans-serif]">
            <div className="bg-white p-8 rounded-2xl border border-zinc-200/80 shadow-xs">
              <div className="w-12 h-12 bg-zinc-100 text-zinc-900 rounded-xl flex items-center justify-center mx-auto mb-4">
                <MessageSquare size={22} />
              </div>
              <h3 className="text-sm sm:text-base font-semibold text-zinc-900 tracking-tight mb-2">
                Launch Configuration
              </h3>
              <p className="text-xs text-zinc-500 leading-relaxed">
                Your campaign will show to{" "}
                <span className="font-semibold text-zinc-900">
                  {conditions.audience === "all"
                    ? "all"
                    : conditions.audience === "first_time"
                    ? "first-time"
                    : "returning"}
                </span>{" "}
                visitors after a{" "}
                <span className="font-semibold text-zinc-900">{conditions.delay} second</span> delay.
              </p>

              {conditions.rules.length > 0 && (
                <div className="mt-5 pt-5 border-t border-gray-100">
                  <div className="flex items-center justify-center gap-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                    <Target size={12} /> {conditions.rules.length} Custom Targeting Rule
                    {conditions.rules.length > 1 ? "s" : ""}
                  </div>
                </div>
              )}

              {(conditions.priority || 0) > 0 && (
                <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-indigo-50 text-indigo-600 rounded-xl text-[10px] font-bold uppercase tracking-widest">
                  Priority Level: P{conditions.priority}
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

export default ConditionEditor;
